import { groq } from "next-sanity";
import { getClient } from "./client";
// Import sanityFetch dynamically to avoid module evaluation during client bundling
// This ensures defineLive is only called in server component contexts at runtime
// The dynamic import pattern prevents Next.js from analyzing live.ts during build
import type { NavigationData } from "@/types/navigation";

// Lazy import sanityFetch to avoid module evaluation in client components
// This ensures live.ts (which calls defineLive) is only loaded in server contexts
async function getSanityFetch() {
  const { sanityFetch } = await import("./live");
  return sanityFetch;
}

/**
 * Optimized GROQ query for fetching page data by slug
 * Uses array dereferencing (@->) for efficient reference resolution
 * See: https://www.sanity.io/docs/developer-guides/query-optimization
 */
export const pageBySlugQuery = groq`
*[_type == "page" && (
  slug.current == $slug || 
  ($slug == "" && (slug.current == "/" || slug.current == "" || !defined(slug.current)))
)][0]{
  _id,
  title,
  "slug": slug.current,
  seo,
  content,
  sections[]{
    _key,
    ...(@->){
      _id,
      _type,
        // Hero
        ...select(
          _type == "heroSection" => {
            layout,
            title,
            subtitle,
            description,
            buttons,
            backgroundImage,
            showBackgroundGradient,
            titleGradient
          },
        // Companies
        _type == "companiesSection" => {
          showHeader,
          title,
          subtitle,
          companies,
          // Fetch logos: if companies array exists and has items, use those references; otherwise fetch all
          // Filter out null references (broken/draft references) before ordering
          "logos": select(
            defined(companies) && count(companies) > 0 => 
              companies[]->[_type == "companyLogo" && defined(logo)] | order(coalesce(order, 999) asc, name asc){
                _id,
                name,
                "image": logo,
                "width": logo.asset->metadata.dimensions.width,
                "height": logo.asset->metadata.dimensions.height
              },
            *[_type == "companyLogo" && defined(logo)] | order(coalesce(order, 999) asc, name asc){
              _id,
              name,
              "image": logo,
              "width": logo.asset->metadata.dimensions.width,
              "height": logo.asset->metadata.dimensions.height
            }
          )
        },
        // Competencies
        _type == "competenciesSection" => {
          subtitle,
          title,
          sectionImage,
          "items": *[_type == "competency"] | order(order asc){
            _id,
            title,
            description
          }
        },
        // Expertise
        _type == "expertiseSection" => {
          subtitle,
          title,
          "intro": coalesce(pt::text(intro), ""),
          "items": *[_type == "expertiseCard"] | order(order asc){
            _id,
            title,
            "description": coalesce(pt::text(description), ""),
            icon,
            metrics
          }
        },
        // Services
        _type == "servicesSection" => {
          title,
          subtitle,
          services,
          // Use services array if it exists and has items, otherwise fall back to all service cards
          // Filter out null references (broken/draft references) - GROQ automatically filters nulls from dereferencing
          "services": select(
            defined(services) && count(services) > 0 => 
              services[]->[_type == "serviceCard"] | order(coalesce(order, 999) asc, title asc){
                _id,
                title,
                description,
                icon{
                  _type,
                  asset->{
                    _id,
                    _type,
                    _ref,
                    url,
                    metadata{
                      dimensions{
                        width,
                        height
                      }
                    }
                  }
                }
              },
            *[_type == "serviceCard"] | order(coalesce(order, 999) asc, title asc){
              _id,
              title,
              description,
              icon{
                _type,
                asset->{
                  _id,
                  _type,
                  _ref,
                  url,
                  metadata{
                    dimensions{
                      width,
                      height
                    }
                  }
                }
              }
            }
          )
        },
        // Resources
        _type == "resourcesSection" => {
          title,
          subtitle,
          ctaTitle,
          ctaDescription,
          primaryButtonText,
          primaryButtonHref,
          secondaryButtonText,
          secondaryButtonHref,
          "resources": *[_type == "resourceCard"] | order(order asc){
            _id,
            title,
            description,
            icon
          }
        },
        // Testimonials
        _type == "testimonialsSection" => {
          title,
          subtitle,
          testimonials,
          // Use testimonials array if it exists and has items, otherwise fall back to all testimonials
          "testimonials": select(
            defined(testimonials) && count(testimonials) > 0 => 
              testimonials[]->[_type == "testimonial"] | order(coalesce(order, 999) asc, name asc){
                _id,
                quote,
                name,
                initials,
                title,
                company,
                quoteIcon1,
                quoteIcon2
              },
            *[_type == "testimonial"] | order(coalesce(order, 999) asc, name asc){
              _id,
              quote,
              name,
              initials,
              title,
              company,
              quoteIcon1,
              quoteIcon2
            }
          )
        },
        // Social Proof
        _type == "socialProofSection" => {
          stats,
          // Use stats array if it exists and has items, otherwise fall back to all stats
          "stats": select(
            defined(stats) && count(stats) > 0 => 
              stats[]->[_type == "socialProofStat"] | order(coalesce(order, 999) asc, stat asc){
                _id,
                stat,
                title,
                subtitle,
                iconType,
                icon,
                iconPosition{
                  width,
                  height,
                  top,
                  left
                },
                icons,
                iconPositions[]{
                  width,
                  height,
                  top,
                  left
                }
              },
            *[_type == "socialProofStat"] | order(coalesce(order, 999) asc, stat asc){
              _id,
              stat,
              title,
              subtitle,
              iconType,
              icon,
              iconPosition{
                width,
                height,
                top,
                left
              },
              icons,
              iconPositions[]{
                width,
                height,
                top,
                left
              }
            }
          )
        },
        // Better Delivery
        _type == "betterDeliverySection" => {
          title,
          description,
          cards,
          // Use cards array if it exists and has items, otherwise fall back to all cards
          "cards": select(
            defined(cards) && count(cards) > 0 => 
              cards[]->[_type == "betterDeliveryCard"] | order(coalesce(order, 999) asc, title asc){
                _id,
                title,
                description,
                icons[]{
                  icon,
                  className
                }
              },
            *[_type == "betterDeliveryCard"] | order(coalesce(order, 999) asc, title asc){
              _id,
              title,
              description,
              icons[]{
                icon,
                className
              }
            }
          )
        },
        // FAQ
        _type == "faqSection" => {
          title,
          subtitle,
          description,
          displayStyle,
          featuredFaqs,
          "faqs": select(
            defined(featuredFaqs) && count(featuredFaqs) > 0 => 
              featuredFaqs[]->[_type == "faqItem"] | order(order asc){
                _id,
                question,
                answer
              },
            *[_type == "faqItem"] | order(order asc){
              _id,
              question,
              answer
            }
          )
        },
        // Pricing
        _type == "pricingSection" => {
          title,
          subtitle,
          "plans": *[_type == "pricingPlan"] | order(order asc){
            _id,
            name,
            price,
            features,
            highlighted
          }
        },
        // Approach
        _type == "approachSection" => {
          title,
          primaryParagraph,
          secondaryParagraph,
          linkText,
          linkHref,
          "items": *[_type == "approachItem"] | order(coalesce(order, 999) asc){
            _id,
            text,
            icon
          }
        },
        // Free Templates
        _type == "freeTemplatesSection" => {
          title,
          "description": coalesce(description, ""),
          buttonText,
          disclaimerText,
          icon
        },
        // DevFlowOps
        _type == "devFlowOpsSection" => {
          title,
          "description": coalesce(pt::text(description), ""),
          features,
          asideHeading,
          asideDescription,
          primaryButtonText,
          primaryButtonHref,
          secondaryButtonText,
          secondaryButtonHref,
          "features": select(
            defined(features) && count(features) > 0 => 
              features[]->[_type == "devFlowOpsFeature"] | order(coalesce(order, 999) asc, title asc){
                _id,
                title,
                description,
                icon
              },
            *[_type == "devFlowOpsFeature"] | order(coalesce(order, 999) asc, title asc){
              _id,
              title,
              description,
              icon
            }
          )
        },
        // Solutions Hero
        _type == "solutionsHero" => {
          title,
          description
        },
        // About Hero Section
        _type == "aboutHeroSection" => {
          profileImage,
          title,
          descriptionPart1,
          descriptionPart2
        },
        // About Content Section
        _type == "aboutContentSection" => {
          sections[] {
            heading,
            body
          }
        },
        // Contact Hero Section
        _type == "contactHeroSection" => {
          title,
          description
        },
        // Image Text Component
        _type == "imageTextComponent" => {
          layout,
          heading,
          boldParagraph,
          regularParagraph,
          image,
          imageAlt
        },
        // Generic CTA Block
        _type == "genericCtaBlock" => {
          title,
          boldParagraph,
          regularParagraph,
          buttons[]{
            text,
            href,
            variant
          },
          backgroundColor,
          isLastSection
        },
        // Work Experience Section
        _type == "workExperienceSection" => {
          professionalExperience[]{
            title,
            dateRange,
            description
          },
          education[]{
            title,
            description
          },
          certifications[]{
            title,
            dateRange,
            description
          }
        }
      )
    }
  }
}
`;

/**
 * GROQ query for fetching contact page by slug
 */
export const contactPageBySlugQuery = groq`
*[_type == "contactPage" && slug.current == $slug][0]{
  _id,
  "slug": slug.current,
  title,
  intro,
  heroSection->{
    _id,
    _type,
    title,
    description
  },
  formOptions{
    howFoundOptions[]{
      value,
      label
    },
    serviceOptions[]{
      value,
      label
    },
    budgetOptions[]{
      value,
      label
    }
  },
  thankYouPage{
    title,
    message
  },
  seo{
    metaTitle,
    metaDescription,
    ogImage,
    ogTitle,
    ogDescription,
    noIndex
  }
}
`;

/**
 * GROQ query for fetching policy page by slug
 */
export const policyPageBySlugQuery = groq`
*[_type == "policyPage" && slug.current == $slug][0]{
  _id,
  title,
  "slug": slug.current,
  policyType,
  cookiebotScriptId,
  mainContent,
  content[]{
    heading,
    body
  },
  seo{
    metaTitle,
    metaDescription,
    ogImage,
    ogTitle,
    ogDescription,
    noIndex
  }
}
`;

/**
 * Type definitions for Sanity content
 * These types ensure type safety when working with Sanity data
 */

export type SanityImage = {
  _type: string;
  asset: {
    _ref: string;
    _type: string;
  };
};

export type PageSection =
  | {
      _key: string;
      _id: string;
      _type: "heroSection";
      layout?: "centered" | "left-right" | "right-left";
      title?: string;
      subtitle?: any; // Portable text array
      description?: string;
      buttons?: {
        text?: string;
        href?: string;
        variant?: "primary" | "secondary";
      }[];
      backgroundImage?: SanityImage;
      showBackgroundGradient?: boolean;
      titleGradient?: "purple-cyan" | "blue-teal";
    }
  | {
      _key: string;
      _id: string;
      _type: "companiesSection";
      showHeader?: boolean;
      title?: string;
      subtitle?: string;
      logos?: {
        _id: string;
        name?: string;
        image?: SanityImage;
        width?: number;
        height?: number;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "competenciesSection";
      subtitle?: string;
      title?: string;
      sectionImage?: SanityImage;
      items?: {
        _id: string;
        title?: string;
        description?: string;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "expertiseSection";
      subtitle?: string;
      title?: string;
      intro?: string;
      items?: {
        _id: string;
        title?: string;
        description?: string;
        icon?: SanityImage;
        metrics?: { label?: string; value?: string }[];
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "servicesSection";
      title?: string;
      subtitle?: string;
      services?: {
        _id: string;
        title?: string;
        description?: string;
        icon?: SanityImage;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "resourcesSection";
      title?: string;
      subtitle?: string;
      ctaTitle?: string;
      ctaDescription?: string;
      primaryButtonText?: string;
      primaryButtonHref?: string;
      secondaryButtonText?: string;
      secondaryButtonHref?: string;
      resources?: {
        _id: string;
        title?: string;
        description?: string;
        icon?: SanityImage;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "faqSection";
      title?: string;
      subtitle?: string;
      description?: string;
      displayStyle?: "accordion" | "grid" | "list";
      faqs?: {
        _id: string;
        question?: string;
        answer?: any; // Portable text array
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "pricingSection";
      title?: string;
      subtitle?: string;
      plans?: {
        _id: string;
        name?: string;
        price?: string;
        features?: string[];
        highlighted?: boolean;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "approachSection";
      title?: string;
      primaryParagraph?: string;
      secondaryParagraph?: string;
      linkText?: string;
      linkHref?: string;
      items?: {
        _id: string;
        text?: string;
        icon?: SanityImage;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "freeTemplatesSection";
      title?: string;
      description?: string;
      buttonText?: string;
      disclaimerText?: string;
      icon?: SanityImage;
    }
  | {
      _key: string;
      _id: string;
      _type: "testimonialsSection";
      title?: string;
      subtitle?: string;
      testimonials?: {
        _id: string;
        quote?: string;
        name?: string;
        initials?: string;
        title?: string;
        company?: string;
        quoteIcon1?: SanityImage;
        quoteIcon2?: SanityImage;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "socialProofSection";
      stats?: {
        _id: string;
        stat?: string;
        title?: string;
        subtitle?: string;
        iconType?: "single" | "dual";
        icon?: SanityImage;
        iconPosition?: {
          width?: string;
          height?: string;
          top?: string;
          left?: string;
        };
        icons?: SanityImage[];
        iconPositions?: Array<{
          width?: string;
          height?: string;
          top?: string;
          left?: string;
        }>;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "betterDeliverySection";
      title?: string;
      description?: string;
      cards?: {
        _id: string;
        title?: string;
        description?: string;
        icons?: Array<{
          icon?: SanityImage;
          className?: string;
        }>;
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "devFlowOpsSection";
      title?: string;
      description?: string;
      features?: {
        _id: string;
        title?: string;
        description?: string;
        icon?: SanityImage;
      }[];
      asideHeading?: string;
      asideDescription?: string;
      primaryButtonText?: string;
      primaryButtonHref?: string;
      secondaryButtonText?: string;
      secondaryButtonHref?: string;
    }
  | {
      _key: string;
      _id: string;
      _type: "solutionsHero";
      title?: string;
      description?: string;
    }
  | {
      _key: string;
      _id: string;
      _type: "aboutHeroSection";
      profileImage?: SanityImage;
      title?: string;
      descriptionPart1?: string;
      descriptionPart2?: string;
    }
  | {
      _key: string;
      _id: string;
      _type: "aboutContentSection";
      sections?: {
        heading?: string;
        body?: any; // Portable text array
      }[];
    }
  | {
      _key: string;
      _id: string;
      _type: "contactHeroSection";
      title?: string;
      description?: any; // Portable text array
    }
  | {
      _key: string;
      _id: string;
      _type: "imageTextComponent";
      layout?: "text-left-image-right" | "text-right-image-left" | "text-top-image-bottom" | "image-top-text-bottom";
      heading?: string;
      boldParagraph?: string;
      regularParagraph?: string;
      image?: SanityImage;
      imageAlt?: string;
    }
  | {
      _key: string;
      _id: string;
      _type: "genericCtaBlock";
      title?: string;
      boldParagraph?: string;
      regularParagraph?: string;
      buttons?: {
        text?: string;
        href?: string;
        variant?: "primary" | "secondary";
      }[];
      backgroundColor?: "purple" | "blue" | "green" | "transparent";
      isLastSection?: boolean;
    }
  | {
    _key: string;
    _id: string;
    _type: "workExperienceSection";
    professionalExperience?: {
      title?: string;
      dateRange?: string;
      description?: string;
    }[];
    education?: {
      title?: string;
      description?: string;
    }[];
    certifications?: {
      title?: string;
      dateRange?: string;
      description?: string;
    }[];
  }
  | {
    _key: string;
    _id: string;
    _type: "focusAreasSection";
    title?: string;
    subtitle?: string;
    focusAreas?: Array<{
      _id: string;
      _key?: string;
      title?: string;
      description?: string;
      icon?: SanityImage;
    }>;
  };

export type PageWithSections = {
  _id: string;
  title: string;
  slug: string;
  content?: any[]; // Portable text array for page content
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: SanityImage;
    ogTitle?: string;
    ogDescription?: string;
    noIndex?: boolean;
  };
  sections?: PageSection[];
};

/**
 * Fetch a page by its slug from Sanity
 * 
 * @param slug - The page slug (e.g., "home", "about")
 * @param options - Optional Next.js revalidation options
 * @returns The page data or null if not found
 * 
 * @example
 * ```ts
 * const page = await fetchPageBySlug('home')
 * // Revalidate with tags for on-demand revalidation
 * const page = await fetchPageBySlug('home', {
 *   next: { tags: ['page:home'] }
 * })
 * ```
 * 
 * @see https://www.sanity.io/docs/developer-guides/query-optimization
 * @see https://nextjs.org/docs/app/api-reference/functions/next-cache
 */
export async function fetchPageBySlug(
  slug: string,
  options?: { next?: { revalidate?: number | false; tags?: string[] } }
): Promise<PageWithSections | null> {
  try {
    // Normalize slug: "/" becomes empty string for query, but keep original for tags
    const querySlug = slug === "/" ? "" : slug;
    const tagSlug = slug === "/" ? "/" : slug;
    
    // In development, disable caching for hot reloading
    // This ensures new content published in Sanity appears immediately without cache invalidation
    // In production, use revalidation tags for on-demand revalidation via webhooks
    const isDev = process.env.NODE_ENV === "development";
    const defaultNext = {
      revalidate: 3600 as number | false,
      tags: ['sanity', `page:${tagSlug}`],
    };
    const mergedNext = options?.next
      ? {
          revalidate: options.next.revalidate ?? defaultNext.revalidate,
          tags: Array.from(new Set([...(options.next.tags || []), ...defaultNext.tags])),
        }
      : defaultNext;

    const cacheOptions = isDev
      ? {
          // Disable caching in development for immediate updates (hot reloading)
          // This overrides any revalidate options passed from page components
          cache: "no-store" as const,
        }
      : {
          next: mergedNext,
        };
    
    // Use sanityFetch for Content Source Maps (CSM) support in Visual Editing
    // sanityFetch automatically uses the correct client based on draft mode
    const sanityFetch = await getSanityFetch();
    const { data: page } = await sanityFetch({
      query: pageBySlugQuery,
      params: { slug: querySlug },
      ...cacheOptions,
    }) as { data: PageWithSections | null };

    
    return page;
  } catch (error) {
    // Log error for debugging but don't expose details to users
    console.error(`Error fetching page with slug "${slug}":`, error);
    // Return null to allow graceful degradation
    return null;
  }
}

/**
 * Type definitions for Contact Page
 */
export type ContactPageFormOption = {
  value: string;
  label: string;
};

export type ContactPageFormOptions = {
  howFoundOptions?: ContactPageFormOption[];
  serviceOptions?: ContactPageFormOption[];
  budgetOptions?: ContactPageFormOption[];
};

export type ContactPageThankYou = {
  title?: string;
  message?: string;
};

export type ContactPage = {
  _id: string;
  slug?: string;
  title?: string;
  intro?: any; // Portable text array
  heroSection?: {
    _id: string;
    _type: string;
    title?: string;
    description?: any; // Portable text array
  };
  formOptions?: ContactPageFormOptions;
  thankYouPage?: ContactPageThankYou;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: SanityImage;
    ogTitle?: string;
    ogDescription?: string;
    noIndex?: boolean;
  };
};

/**
 * Type definitions for Policy Page
 */
export type PolicyPageContentSection = {
  heading?: string;
  body?: any; // Portable text array
};

export type PolicyPage = {
  _id: string;
  title?: string;
  slug?: string;
  policyType?: string;
  cookiebotScriptId?: string;
  mainContent?: any[]; // Portable text array
  content?: PolicyPageContentSection[];
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: SanityImage;
    ogTitle?: string;
    ogDescription?: string;
    noIndex?: boolean;
  };
};

/**
 * Fetch a contact page by its slug from Sanity
 * 
 * @param slug - The contact page slug (e.g., "contact")
 * @param options - Optional Next.js revalidation options
 * @returns The contact page data or null if not found
 * 
 * @example
 * ```ts
 * const contactPage = await fetchContactPageBySlug('contact')
 * ```
 */
export async function fetchContactPageBySlug(
  slug: string,
  options?: { next?: { revalidate?: number | false; tags?: string[] } }
): Promise<ContactPage | null> {
  try {
    const isDev = process.env.NODE_ENV === "development";
    const defaultNext = {
      revalidate: 3600 as number | false,
      tags: ['sanity', `contactPage:${slug}`],
    };
    const mergedNext = options?.next
      ? {
          revalidate: options.next.revalidate ?? defaultNext.revalidate,
          tags: Array.from(new Set([...(options.next.tags || []), ...defaultNext.tags])),
        }
      : defaultNext;

    const cacheOptions = isDev
      ? { cache: "no-store" as const }
      : {
          next: mergedNext,
        };
    
    // Use sanityFetch for Content Source Maps (CSM) support in Visual Editing
    const sanityFetch = await getSanityFetch();
    const { data: page } = await sanityFetch({
      query: contactPageBySlugQuery,
      params: { slug },
      ...cacheOptions,
    }) as { data: ContactPage | null };

    if (process.env.NODE_ENV === "development" && !page) {
    }
    
    return page;
  } catch (error) {
    console.error(`Error fetching contact page with slug "${slug}":`, error);
    return null;
  }
}

/**
 * Fetch a policy page by its slug from Sanity
 * 
 * @param slug - The policy page slug (e.g., "cookie", "privacy")
 * @param options - Optional Next.js revalidation options
 * @returns The policy page data or null if not found
 * 
 * @example
 * ```ts
 * const policyPage = await fetchPolicyPageBySlug('cookie')
 * ```
 */
export async function fetchPolicyPageBySlug(
  slug: string,
  options?: { next?: { revalidate?: number | false; tags?: string[] } }
): Promise<PolicyPage | null> {
  try {
    const isDev = process.env.NODE_ENV === "development";
    const defaultNext = {
      revalidate: 3600 as number | false,
      tags: ['sanity', `policyPage:${slug}`],
    };
    const mergedNext = options?.next
      ? {
          revalidate: options.next.revalidate ?? defaultNext.revalidate,
          tags: Array.from(new Set([...(options.next.tags || []), ...defaultNext.tags])),
        }
      : defaultNext;

    const cacheOptions = isDev
      ? { cache: "no-store" as const }
      : {
          next: mergedNext,
        };
    
    // Use sanityFetch for Content Source Maps (CSM) support in Visual Editing
    const sanityFetch = await getSanityFetch();
    const { data: page } = await sanityFetch({
      query: policyPageBySlugQuery,
      params: { slug },
      ...cacheOptions,
    }) as { data: PolicyPage | null };

    if (process.env.NODE_ENV === "development" && !page) {
    }
    
    return page;
  } catch (error) {
    console.error(`Error fetching policy page with slug "${slug}":`, error);
    return null;
  }
}

/**
 * Unified type for all page types
 */
export type AnyPage = 
  | (PageWithSections & { _pageType: 'page' })
  | (ContactPage & { _pageType: 'contactPage' })
  | (PolicyPage & { _pageType: 'policyPage' });

/**
 * Fetch any page type by slug (tries page, contactPage, then policyPage)
 * 
 * @param slug - The page slug
 * @param options - Optional Next.js revalidation options
 * @returns The page data with _pageType indicator or null if not found
 * 
 * @example
 * ```ts
 * const page = await fetchAnyPageBySlug('contact')
 * if (page?._pageType === 'contactPage') {
 *   // Handle contact page
 * }
 * ```
 */
export async function fetchAnyPageBySlug(
  slug: string,
  options?: { next?: { revalidate?: number | false; tags?: string[] } }
): Promise<AnyPage | null> {
  // For "contact" slug, ALWAYS check contactPage first and skip regular pages
  // This ensures contactPage takes precedence over any regular page with slug "contact"
  if (slug === "contact") {
    const contactPage = await fetchContactPageBySlug("contact", options);
    if (contactPage) {
      return { ...contactPage, _pageType: 'contactPage' as const };
    }
    // If no contactPage found, still check regular pages as fallback
    const page = await fetchPageBySlug(slug, options);
    if (page) {
      return { ...page, _pageType: 'page' as const };
    }
    return null;
  }

  // For policy slugs, check policyPage first to ensure mainContent is used
  const policyPageSlugs = ['terms-of-service', 'privacy-statement', 'cookie-policy', 'terms', 'privacy', 'cookie'];
  if (policyPageSlugs.includes(slug)) {
    const policyPage = await fetchPolicyPageBySlug(slug, options);
    if (policyPage) {
      return { ...policyPage, _pageType: 'policyPage' as const };
    }
  }

  // For all other slugs, try regular page first
  const page = await fetchPageBySlug(slug, options);
  if (page) {
    return { ...page, _pageType: 'page' as const };
  }

  // Try contact page (for other slugs that might be contact pages)
  const contactPage = await fetchContactPageBySlug(slug, options);
  if (contactPage) {
    return { ...contactPage, _pageType: 'contactPage' as const };
  }

  // Try policy page (fallback for any other policy slugs not in the list above)
  const policyPage = await fetchPolicyPageBySlug(slug, options);
  if (policyPage) {
    return { ...policyPage, _pageType: 'policyPage' as const };
  }

  return null;
}

/**
 * GROQ query for fetching main navigation and published pages
 * Note: This query uses the 'published' perspective, so only published documents are returned
 */
export const mainNavigationQuery = groq`
{
  "navigation": *[_type == "navigation" && name == "main-navigation"][0]{
    _id,
    name,
    links[]{
      label,
      url,
      external
    }
  },
  "pages": *[_type == "page" && defined(slug.current)] | order(title asc){
    _id,
    title,
    "slug": slug.current
  }
}
`;

/**
 * Fetch main navigation data from Sanity
 * 
 * @param options - Optional Next.js revalidation options
 * @returns Navigation data including navigation document and published pages
 * 
 * @example
 * ```ts
 * const navData = await fetchMainNavigation()
 * ```
 */
export async function fetchMainNavigation(
  options?: { next?: { revalidate?: number | false; tags?: string[] } }
): Promise<NavigationData> {
  try {
    const isDev = process.env.NODE_ENV === "development";
    const defaultNext = {
      revalidate: 3600 as number | false,
      tags: ['sanity', 'navigation', 'page'],
    };
    const mergedNext = options?.next
      ? {
          revalidate: options.next.revalidate ?? defaultNext.revalidate,
          tags: Array.from(new Set([...(options.next.tags || []), ...defaultNext.tags])),
        }
      : defaultNext;

    const cacheOptions = isDev
      ? { cache: "no-store" as const }
      : {
          next: mergedNext,
        };
    
    // Use sanityFetch for Content Source Maps (CSM) support in Visual Editing
    const sanityFetch = await getSanityFetch();
    const { data } = await sanityFetch({
      query: mainNavigationQuery,
      params: {},
      ...cacheOptions,
    }) as { data: NavigationData };

    // Ensure we always return a valid structure
    const result: NavigationData = {
      navigation: data?.navigation || null,
      pages: data?.pages || [],
    };

    return result;
  } catch (error) {
    console.error(`Error fetching main navigation:`, error);
    // Return empty navigation on error for graceful degradation
    return { navigation: null, pages: [] };
  }
}

